import React, { useRef } from 'react';
import { connect } from "react-redux";
import { useImmer } from 'use-immer';
import { useEffect } from "react";
import {
    Box,
    ClickAwayListener,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    CircularProgress // <-- Add this import
} from '@mui/material';
import '../BooksOrderConfirmComponent/BooksOrderConfirmComponent.css';
import InfoIcon from '@mui/icons-material/Info';
import { setorderBookingDetails, booksUserDetails } from '../../store/actions/book.js';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from 'axios';
import * as config from "../../assets/js/constant.js";

const BooksOrderConfirmComponent = ({
    getuserDetails,
    setuserDetails,
    bookOrderDetails,
    shippingDetails,
    shippingCharges,
    setorderBookingDetails,
    booksUserDetails,
    onShippingLoadingChange // <-- Add this prop
}) => {
    const [open, setOpen] = useImmer(false);
    const [bookCounts, setBookCounts] = useImmer(() => {
        const counts = {};
        bookOrderDetails.forEach(book => {
            counts[book.BookID] = book.Copy;
        });
        return counts;
    });
    const [shippingLoading, setShippingLoading] = useImmer(false); // <-- Loader for all totals
    const lastRequestedShippingCost = useRef(null); // Track last requested shipping cost

    useEffect(() => {
        setBookCounts(draft => {
            bookOrderDetails.forEach(book => {
                draft[book.BookID] = book.Copy;
            });
        });
    }, [bookOrderDetails]);

    const handleIncrement = (book) => {
        setShippingLoading(true); // Start loader
        setorderBookingDetails({ ...book, increment: true });
        setBookCounts(draft => {
            draft[book.BookID] = (draft[book.BookID] || 0) + 1;
        });
        // Loader will be stopped in updateShippingCharges finally block
    };

    const handleDecrement = (book) => {
        setShippingLoading(true); // Start loader
        setBookCounts(draft => {
            if (draft[book.BookID] > 1) {
                draft[book.BookID] -= 1;
            }
        });
        setorderBookingDetails({ ...book, decrement: true });
        // Loader will be stopped in updateShippingCharges finally block
    };

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(true);
    };

    // Function to call shipmentCharges API and update shipping charges
    const updateShippingCharges = React.useCallback(async (updatedBooks) => {
        if (!shippingDetails?.bookUserPincode) {
            setShippingLoading(false); // Stop loader if no pincode
            return;
        }
        setShippingLoading(true);
        try {
            const response = await axios.post(
                config.API_BASE_URL + 'apiCall.php?apicall=shipmentCharges',
                {
                    pincode: shippingDetails.bookUserPincode,
                    books: updatedBooks
                }
            );
            if (response.data.Status) {
                // Store the expected new value
                lastRequestedShippingCost.current = response.data.Data.totalShippingCost;
                booksUserDetails({
                    pincode: shippingDetails.bookUserPincode,
                    books: updatedBooks
                });
            }
        } catch (err) {
            setShippingLoading(false); // Stop loader on error
        }
        // Do NOT stop loader here; wait for Redux update
    }, [shippingDetails, booksUserDetails, setShippingLoading]);

    // Watch for bookCounts change and update shipping charges
    useEffect(() => {
        // Prepare updated books array
        const updatedBooks = bookOrderDetails.map(book => ({
            ...book,
            Copy: bookCounts[book.BookID] || 0
        }));
        updateShippingCharges(updatedBooks);
    }, [bookCounts, bookOrderDetails, updateShippingCharges]);

    // Stop loader only when Redux shippingCharges matches last requested value
    useEffect(() => {
        if (
            lastRequestedShippingCost.current !== null &&
            shippingCharges &&
            shippingCharges.totalShippingCost === lastRequestedShippingCost.current
        ) {
            setShippingLoading(false);
            lastRequestedShippingCost.current = null; // Reset after match
        }
    }, [shippingCharges, setShippingLoading]);

    // Calculate subTotal and update state
    useEffect(() => {
        // Create a new array with updated Copy values
        const updatedBooks = bookOrderDetails.map(book => ({
            ...book,
            Copy: bookCounts[book.BookID] || 0
        }));

        // Calculate subTotal using updatedBooks
        const subTotal = updatedBooks.reduce((acc, book) => acc + book.Copy * book.Amount, 0);
        // Ensure charges is a number
        const charges = Number(shippingCharges.totalShippingCost) || 0;
        const total = subTotal + charges;

        // Update getuserDetails state
        setuserDetails(draft => {
            draft.subTotal = subTotal;
            draft.total = total;
            draft.shippingCharges = charges;
        });
    }, [shippingCharges, bookCounts, bookOrderDetails]); // Add bookOrderDetails to dependencies

    // Notify parent when shippingLoading changes
    useEffect(() => {
        if (onShippingLoadingChange) {
            onShippingLoadingChange(shippingLoading);
        }
    }, [shippingLoading, onShippingLoadingChange]);

    return (
        <>
            <Box
                className='books-ordered-screen'
                component="form"
                sx={{
                    '& .MuiTextField-root': { m: 1 },
                    mx: 20,
                    pt: 6
                }}
                noValidate
                autoComplete="off"
            >
                <div>
                    <Box>
                        <Typography sx={{ pb: 4, fontWeight: '600', color: '#010101' }}>Books you selected</Typography>
                        <TableContainer sx={{ mb: 4 }}>
                            <Table className='order-confirm-table' sx={{}} aria-label="spanning table">
                                <TableHead sx={{ border: '1px solid rgb(224 224 224)' }}>
                                    <TableRow>
                                        <TableCell align="center" sx={{ fontWeight: '600', color: '#010101' }}>Book Title</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: '600', color: '#010101' }}>Copies</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: '600', color: '#010101' }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bookOrderDetails.map((book, index) => {
                                        // Determine if decrement should be disabled
                                        const isLastBook = bookOrderDetails.length === 1;
                                        const isSingleCopy = bookCounts[book.BookID] === 1;
                                        const disableDecrement = isLastBook && isSingleCopy;
                                        return (
                                            <TableRow
                                                key={book.BookID}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, border: '1px solid rgb(224 224 224)' }}
                                            >
                                                <TableCell className='order-books-name' component="th" scope="row" sx={{ fontWeight: '600', verticalAlign: 'middle' }}>
                                                    {book.BookName}
                                                </TableCell>
                                                {/* <TableCell align="center" sx={{ color: '#8E8E8E', fontSize: '16px' }}>{book.Copy}</TableCell> */}
                                                <TableCell align="center" sx={{ color: '#8E8E8E', fontSize: '16px', verticalAlign: 'middle' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconButton
                                                            aria-label="reduce"
                                                            onClick={() => handleDecrement(book)}
                                                            sx={{ minWidth: '40px' }}
                                                            disabled={disableDecrement}
                                                        >
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography sx={{ mx: 2 }}>{bookCounts[book.BookID]}</Typography>
                                                        <IconButton
                                                            aria-label="increase"
                                                            onClick={() => handleIncrement(book)}
                                                            sx={{ minWidth: '40px' }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ verticalAlign: 'middle', p: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <CurrencyRupeeIcon sx={{ fontSize: '16px', color: '#8E8E8E', mr: 0.5 }} />
                                                        <Typography sx={{ color: '#8E8E8E', fontSize: '16px', minWidth: 40, textAlign: 'left' }}>
                                                            {book.Amount}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow sx={{ borderBottom: '1px solid #D9D9D9' }}>
                                        <TableCell rowSpan={3} />
                                        <TableCell sx={{ fontWeight: '600', verticalAlign: 'middle', textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                SubTotal
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ verticalAlign: 'middle', p: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <CurrencyRupeeIcon sx={{ fontSize: '16px', color: '#8E8E8E', mr: 0.5 }} />
                                                <Typography sx={{ color: '#8E8E8E', fontSize: '16px', minWidth: 40, textAlign: 'left' }}>
                                                    {shippingLoading ? (
                                                        <CircularProgress size={18} thickness={5} />
                                                    ) : (
                                                        // Always show two decimals for shipping charges
                                                        Number(getuserDetails.subTotal).toFixed(2)
                                                    )}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{ borderBottom: '1px solid #D9D9D9' }}>
                                        <TableCell sx={{ fontWeight: '600', verticalAlign: 'middle', textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                <Box>Shipping</Box>
                                                <Box>
                                                    <ClickAwayListener onClickAway={handleTooltipClose}>
                                                        <div className='tooltip-style'>
                                                            <Tooltip
                                                                arrow
                                                                PopperProps={{
                                                                    disablePortal: true,
                                                                }}
                                                                onClose={handleTooltipClose}
                                                                open={open}
                                                                disableFocusListener
                                                                disableHoverListener
                                                                disableTouchListener
                                                                title="Subject to change based on the delivery address."
                                                            >
                                                                <IconButton onClick={handleTooltipOpen}>
                                                                    <InfoIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    </ClickAwayListener>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ verticalAlign: 'middle', p: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <CurrencyRupeeIcon sx={{ fontSize: '16px', color: '#8E8E8E', mr: 0.5 }} />
                                                <Typography sx={{ color: '#8E8E8E', fontSize: '16px', minWidth: 40, textAlign: 'left' }}>
                                                    {shippingLoading ? (
                                                        <CircularProgress size={18} thickness={5} />
                                                    ) : (
                                                        // Always show two decimals for shipping charges
                                                        Number(getuserDetails.shippingCharges).toFixed(2)
                                                    )}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: '600', borderBottom: '1px solid #D9D9D9', verticalAlign: 'middle', textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                Total
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ verticalAlign: 'middle', p: 0, borderBottom: '1px solid #D9D9D9' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <CurrencyRupeeIcon sx={{ fontSize: '16px', color: '#8E8E8E', mr: 0.5 }} />
                                                <Typography sx={{ color: '#8E8E8E', fontSize: '16px', minWidth: 40, textAlign: 'left' }}>
                                                    {shippingLoading ? (
                                                        <CircularProgress size={18} thickness={5} />
                                                    ) : (
                                                        // Always show two decimals for shipping charges
                                                        Number(getuserDetails.total).toFixed(2)
                                                    )}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box>
                            <Box className="form-control-spacing">
                                <Typography sx={{ pb: 4, fontWeight: '600', color: '#010101' }}>Shipping Address</Typography>
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserName}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserAddress}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserPincode}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>India</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserContact}</Typography>
                                </Box>
                                {shippingDetails.bookUserAlt && (
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserAlt}</Typography>
                                </Box>
                                 )}
                                {shippingDetails.userPanNo && (
                                    <Box>
                                        <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.userPanNo}</Typography>
                                    </Box>
                                )}
                                <Box>
                                    <Typography sx={{ color: '#8E8E8E', pb: 3 }}>{shippingDetails.bookUserEmail}</Typography>
                                </Box>
                                {shippingDetails.bookUserComments && (
                                    <Box>
                                        <Typography sx={{ color: '#8E8E8E', pb: 3, wordBreak: "break-word" }}>{shippingDetails.bookUserComments}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </div>
            </Box>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        bookOrderDetails: state.book.bookOrderDetails,
        shippingDetails: state.book.shippingDetails,
        shippingCharges: state.book.shippingCharges,
    };
};


export default connect(mapStateToProps, {
    setorderBookingDetails,
    booksUserDetails
})(BooksOrderConfirmComponent);
